import type { ReactNode } from "react";
import { Navigate, useParams } from "react-router";

import ApprovalsPage from "@/pages/approvals";
import AppPage from "@/pages/app";
import AccountProfileSettingsPage from "@/pages/account-profile-settings";
import BrandBillingPage from "@/pages/brand-billing";
import { AddBrandPage, BrandOnboardingPage } from "@/pages/brand-onboarding";
import BrandProfilePage from "@/pages/brand-profile";
import BrandSettingsPage from "@/pages/brand-settings";
import BrandSourceMaterialsPage from "@/pages/brand-source-materials";
import BrandStylePage from "@/pages/brand-style";
import BrandVisualUniversePage from "@/pages/brand-visual-universe";
import BrandVoicePage from "@/pages/brand-voice";
import CalendarPage from "@/pages/calendar";
import CampaignDetailPage from "@/pages/campaign-detail";
import CampaignPostPage from "@/pages/campaign-post";
import CampaignsPage from "@/pages/campaigns";
import ContentPreferencesPage from "@/pages/content-preferences";
import FocusPage from "@/pages/focus";
import HomePage from "@/pages/home";
import InsightsPage from "@/pages/insights";
import InstagramIntegrationPage from "@/pages/instagram-integration";
import IntegrationsPage from "@/pages/integrations";
import MediaGalleryPage from "@/pages/media-gallery";
import PulsePage from "@/pages/pulse";
import StandalonePostPage from "@/pages/standalone-post";
import { buildBrandPath } from "@/hooks/use-route-brand";
import {
  BRAND_APP_ROUTE_MANIFEST,
  type AppRouteId,
  type AppRouteManifestEntry,
  PROTECTED_APP_ROUTE_MANIFEST,
  PUBLIC_APP_ROUTE_MANIFEST,
} from "./app-route-manifest";

export type AppRouteDefinition = AppRouteManifestEntry & {
  readonly element: ReactNode;
};

function LegacyApprovalsRedirect() {
  const { brandId } = useParams<{ brandId: string }>();

  return <Navigate to={brandId ? buildBrandPath(brandId, "/review") : "/app"} replace />;
}

const APP_ROUTE_ELEMENTS = {
  home: <HomePage />,
  "app-home": <AppPage />,
  "account-profile-settings": <AccountProfileSettingsPage />,
  "brand-new": <AddBrandPage />,
  "brand-onboarding": <BrandOnboardingPage />,
  "brand-pulse": <PulsePage />,
  "brand-pulse-chat": <PulsePage />,
  "brand-calendar": <CalendarPage />,
  "brand-review": <ApprovalsPage />,
  "brand-approvals": <LegacyApprovalsRedirect />,
  "brand-insights": <InsightsPage />,
  "brand-focus": <FocusPage />,
  "brand-campaigns": <CampaignsPage />,
  "brand-campaign-detail": <CampaignDetailPage />,
  "brand-campaign-post": <CampaignPostPage />,
  "brand-standalone-post": <StandalonePostPage />,
  "brand-assets": <MediaGalleryPage />,
  "brand-profile": <BrandProfilePage />,
  "brand-content-preferences": <ContentPreferencesPage />,
  "brand-source-materials": <BrandSourceMaterialsPage />,
  "brand-style": <BrandStylePage />,
  "brand-visual-universe": <BrandVisualUniversePage />,
  "brand-voice": <BrandVoicePage />,
  "brand-settings": <BrandSettingsPage />,
  "brand-settings-billing": <BrandBillingPage />,
  "brand-integrations": <IntegrationsPage />,
  "brand-integration-instagram": <InstagramIntegrationPage />,
} satisfies Record<AppRouteId, ReactNode>;

const withRouteElements = (
  routes: readonly AppRouteManifestEntry[],
): readonly AppRouteDefinition[] =>
  routes.map((route) => ({
    ...route,
    element: APP_ROUTE_ELEMENTS[route.id as AppRouteId],
  }));

export const PUBLIC_APP_ROUTES = withRouteElements(PUBLIC_APP_ROUTE_MANIFEST);
export const PROTECTED_APP_ROUTES = withRouteElements(PROTECTED_APP_ROUTE_MANIFEST);
export const BRAND_APP_ROUTES = withRouteElements(BRAND_APP_ROUTE_MANIFEST);
